import clsx from 'clsx';
import Button from 'components/Button';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import Pill from 'components/Pill';
import TooltipWrapper from 'components/Tooltip';
import {FC} from 'react';
import {msgid, ngettext, t} from 'ttag';

import DropdownContext from '../context';
import Drawer from '../Drawer';
import useDropdown from '../hooks/useDropdown';
import {
    BaseDropdown,
    FieldType,
    MultiChoice,
    OptionsLocal,
    OptionsServer,
    SingleChoice,
} from '../type';
import {field} from './styles.module.css';

export type DropdownSelectProps = {
    hasArrow?: boolean;
    hasClearSelection?: boolean;
    icon?: IconType;
    isSearchable?: boolean;
    placeholder?: string;
    type: Extract<FieldType, 'select'>;
} & BaseDropdown &
    (MultiChoice | SingleChoice) &
    (OptionsLocal | OptionsServer);

const DropdownSelect: FC<DropdownSelectProps> = ({
    disabled,
    drawerClassName,
    error,
    fieldClassName,
    hasArrow = true,
    hasClearSelection,
    icon,
    isCompact,
    isMultiChoices,
    isSearchable,
    kind = 'secondary',
    message,
    onChange,
    options,
    placeholder,
    serverRequest,
    type,
    value,
    ...rest
}) => {
    const providerValue = useDropdown({
        disabled,
        hasClearSelection,
        isSearchable,
        type,
        ...({isMultiChoices, onChange, value} as MultiChoice | SingleChoice),
        ...({options, serverRequest} as OptionsLocal | OptionsServer),
    });

    const {isShown, onClickField, refField, selectedOptions, setTooltipRef} =
        providerValue;

    const getFieldValue = () => {
        if (selectedOptions.length <= 0) {
            return placeholder || t`Select`;
        }

        if (isMultiChoices) {
            const count = selectedOptions.length;

            return ngettext(
                msgid`${count} item selected`,
                `${count} items selected`,
                selectedOptions.length,
            );
        }

        return selectedOptions[0].label;
    };

    const arrowComponent = hasArrow && (
        <Icon
            className="ml-2"
            data-arrow="true"
            icon={isShown ? 'small_arrow_up' : 'small_arrow_down'}
            size="base"
        />
    );

    return (
        <DropdownContext.Provider value={providerValue}>
            <div
                className={clsx(disabled && 'cursor-not-allowed')}
                ref={disabled ? (ref) => setTooltipRef(ref) : null}
                {...rest}
            >
                <div
                    aria-controls="dropdown-select"
                    aria-expanded={isShown}
                    className={clsx(field, fieldClassName)}
                    data-compact={isCompact}
                    data-disabled={!!disabled}
                    data-dropdown={true}
                    data-invalid={!!error}
                    data-kind={kind}
                    data-status={
                        selectedOptions.length > 0 ? 'selected' : 'unselected'
                    }
                    data-type="select"
                    ref={refField}
                    role="combobox"
                    tabIndex={disabled ? -1 : 0}
                >
                    <TooltipWrapper message={message}>
                        <Button
                            className="w-full"
                            disabled={disabled}
                            icon={icon}
                            isAlignedCenter={false}
                            kind={kind}
                            onClick={onClickField}
                            size={isCompact ? 'sm' : 'base'}
                            tabIndex={-1}
                        >
                            <span className="flex w-full items-center justify-between truncate">
                                <span
                                    className={clsx(
                                        'flex gap-2 truncate',
                                        isMultiChoices &&
                                            selectedOptions.length > 0 &&
                                            clsx(
                                                'rounded px-2',
                                                disabled
                                                    ? 'bg-grey-200 text-grey-500'
                                                    : 'bg-grey-800 text-dark-primary',
                                            ),
                                    )}
                                >
                                    <span className="truncate">
                                        {getFieldValue()}
                                    </span>
                                    {!isMultiChoices &&
                                        selectedOptions[0]?.status && (
                                            <Pill
                                                color={
                                                    selectedOptions[0].status
                                                        ?.color ?? 'grey'
                                                }
                                                label={
                                                    selectedOptions[0].status
                                                        .label
                                                }
                                            />
                                        )}
                                </span>
                                {arrowComponent}
                            </span>
                        </Button>
                    </TooltipWrapper>
                </div>
                {isShown && refField.current && (
                    <Drawer className={drawerClassName} />
                )}
            </div>
        </DropdownContext.Provider>
    );
};

export default DropdownSelect;
